import en from '../../locales/en.json';

/**
 * The app's translator, for copy drawn outside a component: a store's toasts and tooltips, and the
 * toast title `LoadingStateModel.setSuccess` gives every caller.
 *
 * A store cannot call `useI18n`, and importing vue-i18n would tie every store test to it. Instead
 * `main.ts` hands this module `i18n.global.t` once, next to `setErrorTranslator`, and every store
 * reads through `translate` (ENG-9133). One setter rather than one per store, which is what the
 * entry store's `setEntryTranslator` would have become.
 */

export type Translator = (key: string, named: Record<string, unknown>, plural: number) => string;

let translator: Translator | undefined;

/** `main.ts` calls this before anything mounts, so a call with none set is a test that set none. */
export const setTranslator = (translate: Translator | undefined): void => {
  translator = translate;
};

const ENGLISH: Record<string, string> = en;

/**
 * vue-i18n's default rule (`pluralDefault` in `@intlify/core-base`) for a message written as
 * `one | many` or `none | one | many`: a fourth choice and beyond is never picked.
 */
const choiceOf = (message: string, plural: number): string => {
  const choices = message.split('|').map(choice => choice.trim());
  if (choices.length === 1) {
    return choices[0];
  }
  const count = Math.abs(plural);
  const index = choices.length === 2 ? (count === 1 ? 0 : 1) : Math.min(count, 2);
  return choices[index];
};

/**
 * The English source string, for a test that sets no translator: interpolated and pluralised the way
 * vue-i18n does for the messages `en.json` holds, so the copy it asserts is the copy a reader sees.
 * A key `en.json` lacks reads as the key.
 */
const english = (key: string, named: Record<string, unknown>, plural: number): string => {
  const message = ENGLISH[key];
  if (message === undefined) {
    return key;
  }
  const values: Record<string, unknown> = { n: plural, count: plural, ...named };
  return choiceOf(message, plural).replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match);
};

/** `key` in the session's language. `named` fills its placeholders, and `plural` picks a choice. */
export const translate = (key: string, named: Record<string, unknown> = {}, plural = 1): string =>
  translator ? translator(key, named, plural) : english(key, named, plural);
